import axios from 'axios';
import { Subject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { Gizmo } from '../../gizmos';
import {
  IError,
  IFlowyFile,
  IGizmo,
  IGizmoData,
  IGizmoEvent,
  IUserV2,
} from '../../types';
import { downloadFileS3 } from './utils';
import { toCamelCase } from '../../utils';
import CommonBinder from '../common-binder';

interface IFileFieldBinder {
  init(): Promise<void>;
  setFiles(files: File[]): Promise<void>;
  // getFiles(): File[] | undefined;
}

class FileFieldBinder extends CommonBinder implements IFileFieldBinder {
  /**
   * List of files
   */
  private _files: File[];
  public files: Subject<File[]>;

  constructor(gizmo: Gizmo) {
    super(gizmo);

    this._files = [];
    this.files = new Subject<File[]>();
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();
      const apiUrl: string | undefined = this._gizmo.getDao()?.getApiUrl();
      const user: IUserV2 | undefined = this._gizmo.getDao()?.getUser();
      const data = this._gizmo.getData();

      if (data && !Array.isArray(data) && apiUrl && user) {
        const platformInterface = this._gizmo
          .getFoundation()
          ?.getPlatformInterface();

        let file: File | undefined;
        if (
          platformInterface &&
          data.fd?.attachmentId &&
          data.fd?.fileExtension
        ) {
          const result = await platformInterface.fileGet({
            path: '',
            fileFid: data.fd?.attachmentId,
            fileExtension: data.fd?.fileExtension,
          });
          file = result?.file;
        }

        if (file) {
          this._files = [file];
          this.files.next(this._files);
        } else {
          if (!config.ops?.fileField?.useApiV2) {
            this._downloadFileS3({ data }).then(file => {
              if (file) {
                this._files = [file];
                this.files.next(this._files);
              }
            });
          } else {
            this._downloadFileV2({ data }).then(file => {
              if (file) {
                this._files = [file];
                this.files.next(this._files);
              }
            });
          }
        }
      } else if (config.ops?.value?.flux && !data) {
        const flux = this._gizmo.getFlux();
        const user = this._gizmo.getDao()?.getUser();

        if (flux && user?.variables) {
          let conditionResult = true;

          if (config.ops.value.condition) {
            const cr = await flux.evaluate({
              script: config.ops.value.condition,
            });

            if (cr !== undefined) {
              conditionResult = cr;
            }
          }

          if (conditionResult === true) {
            const result = await flux.evaluate({
              script: config.ops.value.flux,
            });

            for (const fid in user.variables) {
              const data = user.variables[fid];

              if (data && !Array.isArray(data) && data.v === result) {
                const formId = user.credentials?.['auth-form-fid'];
                const filledFormId = user.id;

                if (!config.ops.fileField?.useApiV2) {
                  this._downloadFileS3({
                    data,
                    fid,
                    formId,
                    filledFormId,
                  }).then(file => {
                    if (file) {
                      this.setFiles([file]);
                    }
                  });
                } else {
                  this._downloadFileV2({
                    data,
                    fid,
                    formId,
                    filledFormId,
                  }).then(file => {
                    if (file) {
                      this.setFiles([file]);
                    }
                  });
                }
              }
            }
          }
        }
      }

      resolve();
    });
  };

  /**
   * Function used to set the files from the UI layer
   *
   * @param {File[]} files files being set by the UI layer
   * @returns
   */
  public setFiles = (files: File[]): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();
      const platformInterface = this._gizmo
        .getFoundation()
        ?.getPlatformInterface();

      // Right now we're going to deal with only one file
      if (files.length === 1) {
        this._files = files;
        this.files.next(files);
        const file = files[0];
        const fileName = file.name;
        const fileExtension = fileName.split('.').pop();

        const attachmentId = uuid();
        const rootGizmoFid = this._gizmo.getCallbacks().getInfoCB({
          type: 'rootGizmoFid',
        });
        const rootGizmoDataFid = this._gizmo.getCallbacks().getInfoCB({
          type: 'rootGizmoDataFid',
        });

        if (platformInterface && fileExtension) {
          await platformInterface.fileSet({
            path: '',
            fileFid: attachmentId,
            fileName,
            fileExtension,
            file,
          });
        }

        await this._gizmo.setData({
          v: attachmentId,
          fd: {
            attachmentId,
            fileName,
            fileExtension,
            fileSize: file.size,
            fileType: file.type,
          },
        });

        const flowyFile: IFlowyFile = {
          attachmentId,
          rootGizmoDataFid,
          rootGizmoFid,
          file,
          gizmoFid: this._gizmo.getConfig().fid,
        };

        if (config.ops?.fileField?.serverProcessing) {
          const apiUrl: string | undefined = this._gizmo.getDao()?.getApiUrl();
          const userCredentials: any =
            this._gizmo.getDao()?.getUser()?.credentials || {};

          if (apiUrl && userCredentials) {
            try {
              const url = `${apiUrl}/main_api/v1/files/root_gizmo_data/process`;
              const formData = new FormData();
              // formData.append('attachment_id', flowyFile.attachmentId);
              // formData.append('root_gizmo_fid', flowyFile.rootGizmoFid);
              // formData.append(
              //   'root_gizmo_data_fid',
              //   flowyFile.rootGizmoDataFid
              // );
              // formData.append('gizmo_fid', flowyFile.gizmoFid);
              formData.append('gizmo', JSON.stringify(this._gizmo.getConfig()));
              formData.append('file', flowyFile.file);

              const response = await axios.post(url, formData, {
                headers: {
                  ...userCredentials,
                  'Content-Type': 'multipart/form-data',
                },
              });

              if (response.status === 200) {
                const { data } = response;

                if (config.ops.fileField.serverProcessing.type === 'curp') {
                  this._processCurpResponse(data);
                  this._gizmo.getErrors().next([]);
                }
              }
            } catch (e) {
              if (e.response.status === 400) {
                const { data } = e.response;

                if (data.detail) {
                  const error: IError = {
                    fid: uuid(),
                    code: 'file_field.server-processing',
                    message: data.detail,
                  };

                  this._gizmo.getErrors().next([error]);
                }
              }
            }
          }
        }
      } else if (files.length === 0) {
        const data = this._gizmo.getData();
        if (
          data &&
          !Array.isArray(data) &&
          platformInterface &&
          data.fd?.attachmentId
        ) {
          const { attachmentId } = data.fd;

          if (config.ops?.fileField?.serverProcessing?.curp) {
            // Send event to all the gizmos that are configured to receive the curp data
            // so they will clear it
            const curpConfig: any = config.ops.fileField.serverProcessing.curp;

            for (const key in curpConfig) {
              const event: IGizmoEvent = {
                type: 'gizmo.clear-data',
                src: {
                  type: 'gizmo',
                  gizmoFid: config.fid,
                  gizmoUid: config.uid,
                },
                dest: {
                  type: 'gizmo',
                  gizmoUid: curpConfig[key],
                },
              };

              this._gizmo.getEvents().next(event);
            }
          }

          await platformInterface.fileRemove({
            path: '',
            fileFid: attachmentId,
          });
        }
        await this._gizmo.setData(undefined);
      }

      resolve();
    });
  };

  public getFiles = (): File[] => {
    return this._files;
  };

  private _downloadFileS3 = async ({
    data,
    fid,
    formId,
    filledFormId,
  }: IDownloadFileV2): Promise<File | undefined> => {
    return await downloadFileS3({
      data,
      fid,
      formId,
      filledFormId,
      context: {
        gizmo: this._gizmo,
      },
    });
  };

  /**
   * Downloads file from the server from the v2 api
   *
   * @param {IGizmoData} data gizmo data
   * @returns
   */
  private _downloadFileV2 = async ({
    data,
    fid,
    formId,
    filledFormId,
  }: IDownloadFileV2): Promise<File | undefined> => {
    return new Promise<File | undefined>(async resolve => {
      const user: IUserV2 | undefined = this._gizmo.getDao()?.getUser();
      const apiUrl: string | undefined = this._gizmo.getDao()?.getApiUrl();
      const callbacks = this._gizmo.getCallbacks();
      const config: IGizmo = this._gizmo.getConfig();

      if (!formId) {
        formId = callbacks.getInfoCB({ type: 'rootGizmoFid' });
      }
      if (!filledFormId) {
        filledFormId = callbacks.getInfoCB({ type: 'rootGizmoDataFid' });
      }
      const fId = fid || config.fid;
      const answerIndex = '0';
      const attachmentId = data.fd?.attachmentId;
      const extension = data.fd?.fileExtension;

      if (apiUrl && user) {
        const credentials: any = user.credentials;
        let url = `${apiUrl}/v3/forms/${formId}/filled_forms_attachment?`;
        url += `attachmentId=${attachmentId}&formId=${formId}&filledFormId=${filledFormId}&`;
        url += `fId=${fId}&answerIndex=${answerIndex}&extension=${extension}`;

        try {
          const response = await axios({
            method: 'GET',
            url,
            responseType: 'blob',
            headers: {
              ...credentials,
            },
          });

          if (response.status === 200 || response.status === 201) {
            let type = '';
            if (extension === 'pdf') {
              type = 'application/pdf';
            } else if (extension === 'jpg' || extension === 'jpeg') {
              type = 'image/jpeg';
            }

            const file = new File(
              [response.data],
              `${attachmentId}.${extension}`,
              { type }
            );

            return resolve(file);
          }
        } catch (e) {
          console.log('*******************e: ', e);
        }
      }

      resolve(undefined);
    });
  };

  // private _downloadFileV2 = async (data: IGizmoData): Promise<void> => {
  //   return new Promise<void>(async resolve => {
  //     const user: IUserV2 | undefined = this._gizmo.getDao()?.getUser();
  //     const apiUrl: string | undefined = this._gizmo.getDao()?.getApiUrl();
  //     const callbacks = this._gizmo.getCallbacks();
  //     const config: IGizmo = this._gizmo.getConfig();

  //     const formId = callbacks.getInfoCB({ type: 'rootGizmoFid' });
  //     const filledFormId = callbacks.getInfoCB({ type: 'rootGizmoDataFid' });
  //     const fId = config.fid;
  //     const answerIndex = '0';
  //     const attachmentId = data.fd?.attachmentId;
  //     const extension = data.fd?.fileExtension;

  //     if (apiUrl && user) {
  //       const credentials: any = user.credentials;
  //       let url = `${apiUrl}/v3/forms/${formId}/filled_forms_attachment?`;
  //       url += `attachmentId=${attachmentId}&formId=${formId}&filledFormId=${filledFormId}&`;
  //       url += `fId=${fId}&answerIndex=${answerIndex}&extension=${extension}`;

  //       try {
  //         const response = await axios({
  //           method: 'GET',
  //           url,
  //           responseType: 'blob',
  //           headers: {
  //             ...credentials,
  //           },
  //         });

  //         if (response.status === 200 || response.status === 201) {
  //           let type = '';
  //           if (extension === 'pdf') {
  //             type = 'application/pdf';
  //           } else if (extension === 'jpg' || extension === 'jpeg') {
  //             type = 'image/jpeg';
  //           }

  //           const file = new File(
  //             [response.data],
  //             `${attachmentId}.${extension}`,
  //             { type }
  //           );

  //           this._files = [file];
  //           this.files.next(this._files);
  //         }
  //       } catch (e) {
  //         console.log('*******************e: ', e);
  //       }
  //     }

  //     resolve();
  //   });
  // };

  // TODO: We're going to leave this method for when we migrate to fastapi endpoints
  // private _downloadFile = async (data: IGizmoData): Promise<void> => {
  //   return new Promise<void>(async resolve => {
  //       const credentials: any = user.credentials;
  //       const formId = callbacks.getInfoCB({ type: 'rootGizmoFid' });
  //       const filledFormId = callbacks.getInfoCB({ type: 'rootGizmoDataFid' });
  //       const fId = config.fid;
  //       const answerIndex = '0';
  //       const attachmentId = data.fd?.attachmentId;
  //       const extension = data.fd?.fileExtension;

  //       let url = `${apiUrl}/main_api/v1/files/root_gizmo_data?formId=${formId}`;
  //       url += `&filledFormId=${filledFormId}&fId=${fId}&answerIndex=${answerIndex}`;
  //       url += `&attachmentId=${attachmentId}&extension=${extension}`;

  //       try {
  //         const response = await axios({
  //           method: 'GET',
  //           url,
  //           responseType: 'blob',
  //           headers: {
  //             ...credentials,
  //           },
  //         });

  //         if (response.status === 200) {
  //           const file = new File([response.data], `${attachmentId}.jpg`);
  //           this._files = [file];
  //           this.files.next(this._files);
  //         }
  //       } catch (e) {}
  //     resolve();
  //   });
  // }

  /**
   * Process the response from the server when the file is processed. We're going to generate
   * the events to set the data in the gizmos that are configured.
   *
   * @param {any} data data arriving from the server
   * @returns
   */
  private _processCurpResponse = async (data: any): Promise<void> => {
    return new Promise<void>(async resolve => {
      const gizmoConfig = this._gizmo.getConfig();

      if (gizmoConfig.ops?.fileField?.serverProcessing?.curp && data.curp) {
        const curpCamelCase = toCamelCase(data.curp);
        const curpConfig: any = gizmoConfig.ops.fileField.serverProcessing.curp;

        for (const key in curpConfig) {
          const gizmoUid = curpConfig[key];
          const event: IGizmoEvent = {
            type: 'gizmo.set-data',
            src: {
              type: 'gizmo',
              gizmoUid: gizmoConfig.uid,
            },
            dest: {
              type: 'gizmo',
              gizmoUid,
            },
            data: {
              gizmoSetData: {
                value: curpCamelCase[key.replace('GizmoUid', '')],
              },
            },
          };

          this._gizmo.getEvents().next(event);
        }
      }

      resolve();
    });
  };
}

interface IDownloadFileV2 {
  data: IGizmoData;
  fid?: string;
  formId?: string;
  filledFormId?: string;
}

export default FileFieldBinder;
